import { Link, Navigate, useParams } from "react-router-dom";

import { Btn } from "../../lib/Btn";
import { Card } from "../../lib/Card";
import { Icon } from "../../lib/Icon";
import { Pill } from "../../lib/Pill";
import { Slats } from "../../lib/Slats";
import { breadcrumbList, useSeo } from "../../lib/useSeo";
import { STATE_BY_SLUG, STATE_RULES_MARKETING } from "./data/stateRules";
import { TOOLS, TOOL_BY_SLUG } from "./data/tools";
import { ToolWidget } from "./tools/calculators";

const STATE_CROSSLINK_SLUGS = [
  "california", "texas", "florida", "new-york", "illinois", "georgia",
  "pennsylvania", "ohio", "north-carolina", "michigan", "arizona", "washington",
];

/**
 * Renders one of the six tools at:
 *   /tools/{toolSlug}                    — generic, no state defaults
 *   /tools/{toolSlug}/{stateSlug}        — state-pre-filled SEO variant
 *
 * Title, description, lead copy and breadcrumb JSON-LD all vary by
 * (toolSlug, stateSlug). The widget itself is a single React component
 * dispatched through ToolWidget.
 */
export function ToolPage() {
  const { slug, stateSlug } = useParams<{ slug: string; stateSlug?: string }>();
  const tool = slug ? TOOL_BY_SLUG[slug] : undefined;
  const state = stateSlug ? STATE_BY_SLUG[stateSlug] : undefined;

  // SEO setup must be unconditional (hook rule). We pass the path-aware
  // payload below regardless of whether the tool/state is valid; if either
  // is missing we render a redirect after.
  const path = tool
    ? state
      ? `/tools/${tool.slug}/${state.slug}`
      : `/tools/${tool.slug}`
    : "/tools";

  useSeo(
    tool
      ? {
          title: state
            ? `${tool.h1(state.name)} — NestPlot`
            : `${tool.h1()} — NestPlot`,
          description: tool.metaDescription(state?.name),
          path,
          jsonLd: state
            ? breadcrumbList([
                { name: "Home", path: "/" },
                { name: "Tools", path: "/tools" },
                { name: tool.name, path: `/tools/${tool.slug}` },
                { name: state.name, path },
              ])
            : breadcrumbList([
                { name: "Home", path: "/" },
                { name: "Tools", path: "/tools" },
                { name: tool.name, path },
              ]),
        }
      : {
          title: "Tool not found — NestPlot",
          description: "NestPlot doesn't have that tool. Browse the full set.",
          path: "/tools",
          robots: "noindex",
        },
  );

  if (!tool) return <Navigate to="/tools" replace />;

  // If stateSlug present but state not found, drop the state context.
  if (stateSlug && !state) return <Navigate to={`/tools/${tool.slug}`} replace />;

  const lead = tool.lead(state?.name);
  const otherTools = TOOLS.filter((t) => t.slug !== tool.slug).slice(0, 4);

  return (
    <div>
      <section className="border-b border-line">
        <div className="mx-auto max-w-[1320px] px-5 lg:px-8 py-10 lg:py-14">
          <Link
            to="/tools"
            className="text-sub text-[12px] font-mono uppercase tracking-[0.12em] hover:text-ink"
          >
            ← All tools
          </Link>
          <div className="grid lg:grid-cols-[1.4fr_1fr] gap-10 items-end mt-3">
            <div>
              <div className="flex flex-wrap gap-2 mb-3">
                <Pill tone="accent" size="sm">
                  {tool.category}
                </Pill>
                {state && (
                  <Pill tone="ok" size="sm">
                    {state.name} · pre-filled
                  </Pill>
                )}
                <Pill tone="default" size="sm">
                  Free · no signup
                </Pill>
              </div>
              <h1 className="t-display-xl">{state ? tool.h1(state.name) : tool.h1()}</h1>
              <p className="text-sub text-lg mt-4 max-w-[600px]">{lead}</p>
            </div>
            <Slats height={14} bands={5} color="var(--accent)" style={{ width: 120 }} />
          </div>
        </div>
      </section>

      <section className="bg-surface border-b border-line">
        <div className="mx-auto max-w-[1320px] px-5 lg:px-8 py-10 lg:py-12">
          <ToolWidget slug={tool.slug} stateSlug={state?.slug} />
        </div>
      </section>

      <section className="border-b border-line">
        <div className="mx-auto max-w-[1320px] px-5 lg:px-8 py-10">
          <div className="font-mono text-[11px] text-sub tracking-[0.14em] uppercase">
            How operators actually use this
          </div>
          <h2 className="t-display-l mt-2">
            Built from the same statute table NestPlot ships in-product.
          </h2>
          <p className="text-sub mt-4 max-w-[760px]">
            NestPlot auto-generates a {state?.name ?? "state-compliant"} lease the moment
            you create a facility. The {tool.name.toLowerCase()} on this page reads from
            the same per-state self-storage statute table — so it's consistent with what
            you'd get on day-one of using NestPlot, before you've even signed your first
            tenant.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/signup">
              <Btn kind="primary" size="md" iconRight={<Icon name="arrow-right" size={14} />}>
                Start free trial
              </Btn>
            </Link>
            {state && (
              <Link to={`/states/${state.slug}`}>
                <Btn kind="ghost" size="md">
                  See all {state.name} rules →
                </Btn>
              </Link>
            )}
            <Link to="/tools">
              <Btn kind="ghost" size="md">
                Other free tools →
              </Btn>
            </Link>
          </div>
        </div>
      </section>

      <section className="border-b border-line">
        <div className="mx-auto max-w-[1320px] px-5 lg:px-8 py-10 lg:py-12">
          <div className="font-mono text-[11px] text-sub tracking-[0.14em] uppercase">
            {tool.name} in other states
          </div>
          <h2 className="t-display-l mt-2">Run this tool somewhere else.</h2>
          <p className="text-sub mt-2 max-w-[600px]">
            Statute-cited per state — the {tool.name.toLowerCase()} pre-fills
            with the right rules wherever you operate.
          </p>
          <div className="mt-5 grid sm:grid-cols-3 lg:grid-cols-6 gap-2">
            {STATE_CROSSLINK_SLUGS.filter((s) => s !== state?.slug).slice(0, 12).map((slug) => {
              const s = STATE_RULES_MARKETING.find((x) => x.slug === slug);
              if (!s) return null;
              return (
                <Link
                  key={s.slug}
                  to={`/tools/${tool.slug}/${s.slug}`}
                  className="border border-line rounded-md px-3 py-2 text-sm font-semibold hover:bg-paper hover:border-line-strong transition-colors"
                >
                  {s.name} →
                </Link>
              );
            })}
          </div>
          <div className="mt-4">
            <Link to="/states" className="text-accent font-semibold">See all 50 states →</Link>
          </div>
        </div>
      </section>

      <section>
        <div className="mx-auto max-w-[1320px] px-5 lg:px-8 py-10 lg:py-12">
          <div className="font-mono text-[11px] text-sub tracking-[0.14em] uppercase">
            More free tools
          </div>
          <h2 className="t-display-l mt-2">Keep exploring.</h2>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {otherTools.map((t) => (
              <Link
                key={t.slug}
                to={state ? `/tools/${t.slug}/${state.slug}` : `/tools/${t.slug}`}
                className="block group"
              >
                <Card className="h-full hover:shadow-rollup transition-shadow">
                  <div className="px-5 py-4">
                    <div className="font-display font-extrabold text-[15px] uppercase tracking-[-0.005em]">
                      {t.name}
                    </div>
                    <p className="text-sub text-[12.5px] mt-2 line-clamp-2">{t.blurb}</p>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
